import { Promocode, PromocodeStatus } from "../interfaces/promocode";
// @ts-ignore
import { roundToHigherOrder } from "./roundToHigherOrder";

interface DiscountItem {
  _id: string;
  type: string;
  uah: number;
  usd: number;
  otherLanguageId?: string;
}

const itemPrice = (
  item: DiscountItem,
  language: string,
  exchangeRate: number
) =>
  language === "uk"
    ? item.uah !== 0
      ? roundToHigherOrder(item.uah)
      : roundToHigherOrder(item.usd * exchangeRate)
    : item.usd;

export const calculateItemsTotal = (
  items: DiscountItem[],
  quantities: number[],
  language: string,
  exchangeRate: number
) => {
  let total = 0;
  items?.forEach((item, index) => {
    total += itemPrice(item, language, exchangeRate) * (quantities?.[index] || 0);
  });
  return total;
};

const isEligible = (promocode: Promocode, item: DiscountItem) => {
  if (promocode.mode === "products") {
    return (
      promocode.products.includes(item._id) ||
      (!!item.otherLanguageId &&
        promocode.products.includes(item.otherLanguageId))
    );
  }
  if (promocode.mode === "categories") {
    return promocode.categories.includes(item.type);
  }
  return true;
};

export const calculateDiscount = (
  promocode: Promocode | null,
  items: DiscountItem[],
  quantities: number[],
  language: string,
  exchangeRate: number
) => {
  if (!promocode) {
    return 0;
  }

  let eligibleTotal = 0;
  items?.forEach((item, index) => {
    if (isEligible(promocode, item)) {
      eligibleTotal +=
        itemPrice(item, language, exchangeRate) * (quantities?.[index] || 0);
    }
  });

  if (!eligibleTotal) {
    return 0;
  }

  if (promocode.type === "percent") {
    return Math.round((eligibleTotal * promocode.percent) / 100);
  }

  const value = language === "uk" ? promocode.uah : promocode.usd;
  return Math.min(value, eligibleTotal);
};

export const getPromocodeStatus = (promocode: Promocode): PromocodeStatus => {
  if (!promocode.active) {
    return "disabled";
  }
  if (promocode.amount && promocode.used >= promocode.amount) {
    return "finished";
  }
  if (!promocode.withoutTerms) {
    const now = Date.now();
    if (promocode.startDate && new Date(promocode.startDate).getTime() > now) {
      return "planned";
    }
    if (promocode.endDate && new Date(promocode.endDate).getTime() < now) {
      return "expired";
    }
  }
  return "active";
};

export const promocodeStatusColor = (status: PromocodeStatus) => {
  switch (status) {
    case "active":
      return "#35AFA3";
    case "planned":
      return "#C8BEBC";
    case "expired":
      return "#FB4839";
    case "finished":
      return "#F5CD22";
    default:
      return "#5B4E4C";
  }
};

export const promocodeStatusLabel = (status: PromocodeStatus) => {
  switch (status) {
    case "active":
      return "Active";
    case "planned":
      return "Planned";
    case "expired":
      return "Expired";
    case "finished":
      return "Finished";
    default:
      return "Disabled";
  }
};

export const formatPromocodeDate = (date?: string) => {
  if (!date) {
    return "";
  }
  const parsed = new Date(date);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${parsed.getFullYear()}`;
};

export const toInputDate = (date?: string) => {
  if (!date) {
    return "";
  }
  return new Date(date).toISOString().slice(0, 10);
};

export const generatePromocode = (length = 12) => {
  const symbols = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < length; i += 1) {
    code += symbols[Math.floor(Math.random() * symbols.length)];
  }
  return code;
};
